import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { WebhookLog, WebhookLogFilters } from "../../types/webhook";
import { useGetWebhookEndpointLogsQuery } from "../../store/api";

interface WebhookEndpointLogsModalProps {
    isOpen: boolean;
    onClose: () => void;
    endpointId: number;
    endpointName: string;
}

const WebhookEndpointLogsModal: React.FC<WebhookEndpointLogsModalProps> = ({
    isOpen,
    onClose,
    endpointId,
    endpointName,
}) => {
    const { t } = useTranslation(["webhooks", "common"]);
    const [filters, setFilters] = useState<WebhookLogFilters>({
        per_page: 20,
    });

    const { data: logsData, isLoading } = useGetWebhookEndpointLogsQuery({
        id: endpointId,
        filters,
    });

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "success":
                return "bg-green-100 text-green-800";
            case "failed":
                return "bg-red-100 text-red-800";
            case "pending":
                return "bg-yellow-100 text-yellow-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white dark:bg-gray-800">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                            {t("webhooks:logs.endpoint_title")} - {endpointName}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {t("webhooks:logs.endpoint_description")}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {/* Фильтры */}
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t("webhooks:filters.status")}
                            </label>
                            <select
                                value={filters.status || ""}
                                onChange={(e) =>
                                    setFilters({
                                        ...filters,
                                        status:
                                            (e.target.value as
                                                | "success"
                                                | "failed"
                                                | "pending") || undefined,
                                    })
                                }
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                            >
                                <option value="">{t("common:all")}</option>
                                <option value="success">
                                    {t("webhooks:status.success")}
                                </option>
                                <option value="failed">
                                    {t("webhooks:status.failed")}
                                </option>
                                <option value="pending">
                                    {t("webhooks:status.pending")}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t("webhooks:filters.date_from")}
                            </label>
                            <input
                                type="date"
                                value={filters.date_from || ""}
                                onChange={(e) =>
                                    setFilters({
                                        ...filters,
                                        date_from: e.target.value || undefined,
                                    })
                                }
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t("webhooks:filters.date_to")}
                            </label>
                            <input
                                type="date"
                                value={filters.date_to || ""}
                                onChange={(e) =>
                                    setFilters({
                                        ...filters,
                                        date_to: e.target.value || undefined,
                                    })
                                }
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Таблица логов */}
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    {isLoading ? (
                        <div className="p-6">
                            <div className="animate-pulse">
                                <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/4 mb-4"></div>
                                <div className="space-y-3">
                                    {[...Array(5)].map((_, i) => (
                                        <div
                                            key={i}
                                            className="h-12 bg-gray-200 dark:bg-gray-600 rounded"
                                        ></div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : !logsData?.data || logsData.data.length === 0 ? (
                        <div className="px-6 py-8 text-center">
                            <p className="text-gray-500 dark:text-gray-400">
                                {t("webhooks:messages.no_logs")}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 dark:bg-gray-900">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("webhooks:logs.table.status")}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t(
                                                "webhooks:logs.table.event_type"
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t(
                                                "webhooks:logs.table.response_code"
                                            )}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            {t("webhooks:logs.table.sent_at")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                                    {logsData.data.map((log: WebhookLog) => (
                                        <tr
                                            key={log.id}
                                            className="hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800 text-gray-900 dark:text-white"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                                                        log.status
                                                    )}`}
                                                >
                                                    {t(
                                                        `webhooks:status.${log.status}`
                                                    )}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {log.event_type}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {log.response_code || "-"}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                {formatDate(log.sent_at)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="flex justify-end mt-4">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                    >
                        {t("common:close")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WebhookEndpointLogsModal;
